import React, { useEffect, useState } from 'react';

import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next';
import Head from 'next/head';
import { format } from 'date-fns';
import parse from 'html-react-parser';
import styled, { css } from 'styled-components';
import { AnimatePresence, motion } from 'framer-motion';
import ru from 'date-fns/locale/ru';

import { Text, Divider, Carousel, ImageContainer, PlayButton } from 'atoms';
import { SocialSharing, Subscription } from 'molecules';
import { NewsNavigation } from 'organisms';
import { NewsApi } from 'services';
import { convertNewsToApp } from 'mappers';
import { colors, media_sm, media_lg, media_md, transitionPageVariants, transition } from 'theme';
import { Jackpot as JackpotIcon } from 'icons';
import { formatMoneySpaces, useWindowSize } from 'hooks';
import { useDispatch } from 'react-redux';
import { useRouter } from 'next/router';
import { app } from 'actions';
import { Modals } from 'types';
import { routes } from 'core';

export async function getServerSideProps({ query }: GetServerSidePropsContext) {
  const { slug } = query;

  const result = await NewsApi.getBySlug(slug);
  const news = convertNewsToApp(result);

  return {
    props: {
      news,
    },
  };
}

export default function News({ news }: InferGetServerSidePropsType<typeof getServerSideProps>) {
  const [url, setUrl] = useState('');
  const dispatch = useDispatch();
  const { width = 0 } = useWindowSize();
  const router = useRouter();

  const postPlayButtonHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();

    if (width > 768) {
      router.push(`${routes.games}?game=${news.gameUrl}`);
      return;
    }

    dispatch(
      app.setAppModalToStoreAction({
        key: Modals.DownloadApp,
        props: {
          onClose: () => {
            dispatch(app.setAppModalToStoreAction({ key: Modals.DownloadApp, props: null }));
          },
        },
      }),
    );
  };

  useEffect(() => {
    setUrl(window.location.href);
  }, [news]);

  return (
    <>
      <Head>
        <title>{news.seoTitle ?? news.title}</title>
        <meta name="description" content={news.seoDescription ?? news.title} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={news.title} />
        <meta property="og:description" content={news.seoDescription || ''} />
        {news.titleImage && <meta property="og:image" content={news.titleImage} key="image" />}
      </Head>

      <AnimatePresence initial={false}>
        <SlideMotion
          key={news.id}
          initial="slideIn"
          animate="default"
          exit="slideOut"
          variants={transitionPageVariants}
          transition={transition}
        >
          <HeaderInfo>
            <Text color={colors.violet} fontSize="16px">
              {news.category}
            </Text>
            <Text color={colors.lightGrey} fontSize="16px">
              {format(new Date(news.date), 'dd MMMM yyyy', { locale: ru })}
            </Text>
          </HeaderInfo>

          <ParagraphStyled>{news.title}</ParagraphStyled>

          {news.slider.length > 0 ? (
            <Carousel autoplay>
              {news.slider.map((item) => (
                <CarouselImage key={item.id} src={item.imageUrl} alt={news.title} />
              ))}
            </Carousel>
          ) : (
            news.titleImage && (
              <ImageContainerStyled src={news.titleImage} alt={news.title} $isNewGame={Boolean(news.jackpot)}>
                <GameInfo $isJackpot={Boolean(news.jackpot)}>
                  {Boolean(news.jackpot) && (
                    <>
                      <Jackpot>
                        <JackpotTextStyled>
                          <JackpotIcon />
                          Макс. выигрыш
                        </JackpotTextStyled>
                        <JackpotSumTextStyled>{formatMoneySpaces(String(news.jackpot))} BYN</JackpotSumTextStyled>
                      </Jackpot>
                    </>
                  )}
                  {Boolean(news.gameUrl) && <PlayButtonStyled gameUrl={news.gameUrl} onClick={postPlayButtonHandler} />}
                </GameInfo>
              </ImageContainerStyled>
            )
          )}

          <ParsedDescription>{parse(news.firstDescription)}</ParsedDescription>

          {news.videoUrl && (
            <Video
              src={news.videoUrl.replace('watch?v=', 'embed/')}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
          <ParsedDescription>{parse(news.secondDescription)}</ParsedDescription>
          <DividerStyled />
          <SocialSharingStyled url={url} title={news.title} />
          <SubscriptionStyled />
          <DividerStyled />
          <NewsNavigation prevNews={news.previousNews} themeNews={news.themeNews} nextNews={news.nextNews} />
        </SlideMotion>
      </AnimatePresence>
    </>
  );
}

const SlideMotion = styled(motion.div)`
  width: 300px;

  @media (min-width: 320px) {
    width: 313px;
  }
  @media (min-width: 330px) {
    width: 320px;
  }

  ${media_sm} {
    width: 710px;
  }

  ${media_md} {
    width: 750px;
  }

  ${media_lg} {
    width: 1146px;
  }
`;

const HeaderInfo = styled.div`
  display: flex;
  justify-content: space-between;

  ${media_sm} {
    justify-content: flex-start;
    span:last-child {
      margin-left: 20px;
    }
  }
`;

const ParagraphStyled = styled.h1`
  color: ${colors.mainText};
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 0px;
  ${media_sm} {
    margin-top: 8px;
    font-size: 50px;
    line-height: 65px;
  }
`;

const ImageContainerStyled = styled(ImageContainer)<{ $isNewGame: boolean }>`
  ${({ $isNewGame }) => css`
    height: 190px;
    margin-top: 40px;
    margin-bottom: 40px;

    border-radius: 20px;

    position: relative;

    ${$isNewGame &&
    css`
      &::after {
        content: ' ';
        width: 100%;
        height: 100%;
        position: absolute;
        top: 0;
        left: 0;
        background: linear-gradient(rgba(0, 0, 0, 0) 50%, rgba(0, 0, 0, 1));
      }
    `}

    ${media_sm} {
      margin-top: 13px;
      height: 440px;
      margin-bottom: 54px;
    }
  `}
`;

const GameInfo = styled.div<{ $isJackpot: boolean }>`
  width: 100%;
  padding: 0 20px 20px 20px;

  position: absolute;
  bottom: 0;
  z-index: 1;

  display: flex;
  justify-content: ${({ $isJackpot }) => ($isJackpot ? 'space-between' : 'flex-end')};
  align-items: center;

  ${media_sm} {
    padding: 0 30px 30px 30px;
  }
`;

const Jackpot = styled.div`
  display: flex;
  flex-direction: column;
`;

const JackpotTextStyled = styled(Text)`
  display: flex;
  align-items: center;

  color: ${colors.white};
  font-size: 14px;

  svg {
    width: 17px;
    margin-right: 5px;
  }

  ${media_sm} {
    font-size: 20px;
    svg {
      width: 25px;
      margin-right: 5px;
    }
  }
`;

const JackpotSumTextStyled = styled(Text)`
  color: ${colors.white};
  font-weight: bold;
  font-size: 20px;

  ${media_sm} {
    font-size: 34px;
    line-height: 34px;
  }
`;

const PlayButtonStyled = styled(PlayButton)`
  && {
    ${media_sm} {
      width: 160px;
      height: 55px;
      font-size: 20px;
      line-height: 55px;
    }
  }
`;

const ParsedDescription = styled.div`
  padding: 0;

  ul,
  ol {
    list-style-position: outside;
  }

  & li {
    margin-left: 30px;
  }

  img {
    width: 100% !important;
    height: auto !important;
    border-radius: 20px;
  }

  *:not(a):not(a *) {
    font-size: 18px;
    color: ${colors.mainText} !important;
    background-color: transparent !important;
  }

  ${media_lg} {
    > :not(img) {
      margin-left: 200px;
      margin-right: 200px;
    }

    p > img {
      width: calc(100% + 400px) !important;
      height: auto !important;
      margin-left: -200px;
    }
  }
`;

const CarouselImage = styled(ImageContainer)`
  height: 190px;

  border-radius: 20px;

  ${media_sm} {
    height: 440px;
  }
`;

const Video = styled.iframe`
  width: 100%;
  height: 170px;
  border-radius: 20px;
  margin-bottom: 40px;

  ${media_sm} {
    height: 400px;
  }

  ${media_md} {
    height: 420px;
  }

  ${media_lg} {
    height: 640px;
  }
`;

const DividerStyled = styled(Divider)`
  ${media_sm} {
    margin-top: 52px;
  }

  ${media_lg} {
    margin-top: 52px;
    margin-left: 200px;
    margin-right: 200px;
  }
`;

const SocialSharingStyled = styled(SocialSharing)`
  ${media_lg} {
    margin-left: 200px;
    margin-right: 200px;
    margin-top: 43px;
    margin-bottom: 44px;
  }
`;

const SubscriptionStyled = styled(Subscription)`
  ${media_lg} {
    margin-left: 200px;
    margin-right: 200px;
  }
`;
